import { createRoute, Link } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Mono,
  PageHeader,
  ResourceTable,
  SectionCard,
  Select,
  StatusBadge,
  TextInput,
  type Column,
} from '@verydia/ui';
import type { InvitationResource, MemberResource } from '@verydia/contracts';
import { Mail, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { rootRoute } from './root.tsx';
import { describeFailure, useMe } from '../lib/api.ts';
import { useAuth } from '../lib/auth/index.ts';
import { useConsoleContext } from '../lib/console-context.tsx';
import {
  useChangeMemberRole,
  useInvitations,
  useInviteMember,
  useMembers,
  useRevokeInvitation,
  useSetMemberStatus,
} from '../lib/identity-queries.ts';
import {
  canActOn,
  grantableRoles,
  roleLabel,
  ROLE_DESCRIPTIONS,
  type OrganizationRole,
} from '../components/role-labels.ts';

const MEMBERSHIP_TONE = {
  ACTIVE: 'success',
  INVITED: 'info',
  SUSPENDED: 'warning',
  REMOVED: 'neutral',
} as const;

const INVITATION_TONE = {
  PENDING: 'info',
  ACCEPTED: 'success',
  EXPIRED: 'neutral',
  REVOKED: 'neutral',
} as const;

function formatDate(value: string | null): string {
  if (value === null) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/* ------------------------------ invite dialog ----------------------------- */

function InviteDialog({
  organizationId,
  roles,
  open,
  onOpenChange,
}: {
  organizationId: string;
  roles: readonly OrganizationRole[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>(roles[roles.length - 1] ?? 'ORGANIZATION_READ_ONLY');
  const invite = useInviteMember(organizationId);

  const failure = invite.isError ? describeFailure(invite.error) : null;

  const close = () => {
    invite.reset();
    setEmail('');
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Invite a member"
      description="They receive an email from our identity provider and choose their own password. No password is set here."
      footer={
        <>
          <Button onClick={close} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Mail />}
            disabled={invite.isPending || email.trim().length === 0}
            onClick={() => {
              invite.mutate({ email: email.trim(), role }, { onSuccess: close });
            }}
          >
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim().length > 0)
            invite.mutate({ email: email.trim(), role }, { onSuccess: close });
        }}
      >
        <Field
          label="Email address"
          required
          description="The invitation goes to this address. It must be the one they will sign in with."
          error={failure?.message}
        >
          {(props) => (
            <TextInput
              {...props}
              type="email"
              autoComplete="off"
              placeholder="person@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label="Role" required description={ROLE_DESCRIPTIONS[role as OrganizationRole]}>
          {(props) => (
            <Select {...props} value={role} onChange={(event) => setRole(event.target.value)}>
              {roles.map((option) => (
                <option key={option} value={option}>
                  {roleLabel(option)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {failure?.requestId ? (
          <p className="text-[11.5px] text-[var(--vc-text-subtle)]">
            Request <Mono>{failure.requestId}</Mono>
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/* ------------------------------ confirm dialog ---------------------------- */

function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  pending,
  error,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  pending: boolean;
  error: { message: string; requestId: string | null } | null;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      {error ? (
        <div role="alert" className="text-[13px] text-[var(--vc-danger-text)]">
          {error.message}
          {error.requestId ? (
            <p className="mt-1 text-[11.5px] text-[var(--vc-text-subtle)]">
              Request <Mono>{error.requestId}</Mono>
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-[13px] text-[var(--vc-text-muted)]">
          This affects this organization only. It does not change their Verydia account or their
          membership of any other organization.
        </p>
      )}
    </Dialog>
  );
}

/* --------------------------------- page ----------------------------------- */

function TeamPage() {
  const auth = useAuth();
  const context = useConsoleContext();
  const me = useMe(auth.status === 'authenticated');
  const organizationId = context.organization?.id ?? null;

  const members = useMembers(organizationId);
  const invitations = useInvitations(organizationId);
  const changeRole = useChangeMemberRole(organizationId ?? '');
  const setStatus = useSetMemberStatus(organizationId ?? '');
  const revoke = useRevokeInvitation(organizationId ?? '');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    member: MemberResource;
    intent: 'suspend' | 'reactivate' | 'remove';
  } | null>(null);

  const profile = me.data?.data;
  const myMembership = profile?.organizations.find((o) => o.organization_id === organizationId);
  const myRole = myMembership?.role ?? null;
  const isPlatformOperator = (profile?.platform_roles.length ?? 0) > 0;
  const roles = grantableRoles(myRole, isPlatformOperator);
  const canInvite = roles.length > 0;

  const breadcrumbs = [
    { label: context.organization?.name ?? 'Organization', href: '/' },
    { label: 'Team' },
  ];

  const memberColumns: ReadonlyArray<Column<MemberResource>> = [
    {
      key: 'person',
      header: 'Member',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-[var(--vc-text)]">{row.display_name}</span>
          <span className="text-[12px] text-[var(--vc-text-subtle)]">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      width: 'w-52',
      render: (row) => {
        const editable = canActOn(myRole, row.role, isPlatformOperator) && roles.length > 0;
        if (!editable)
          return <span className="text-[var(--vc-text-muted)]">{roleLabel(row.role)}</span>;
        return (
          <Select
            aria-label={`Role for ${row.display_name}`}
            value={row.role}
            disabled={changeRole.isPending}
            onChange={(event) =>
              changeRole.mutate({ membershipId: row.membership_id, role: event.target.value })
            }
          >
            {/*
              The member's current role is always listed, even when this actor
              could not grant it. Omitting it would make the control show the
              wrong value for an owner being viewed by an admin.
            */}
            {(roles.includes(row.role as OrganizationRole)
              ? roles
              : [row.role as OrganizationRole, ...roles]
            ).map((option) => (
              <option key={option} value={option}>
                {roleLabel(option)}
              </option>
            ))}
          </Select>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-32',
      render: (row) => <StatusBadge tone={MEMBERSHIP_TONE[row.status]}>{row.status}</StatusBadge>,
    },
    {
      key: 'joined',
      header: 'Joined',
      width: 'w-32',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{formatDate(row.joined_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: 'w-52',
      render: (row) => {
        if (!canActOn(myRole, row.role, isPlatformOperator)) return null;
        return (
          <div className="flex justify-end gap-1.5">
            {row.status === 'SUSPENDED' ? (
              <Button
                size="sm"
                onClick={() => setPendingAction({ member: row, intent: 'reactivate' })}
              >
                Reactivate
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setPendingAction({ member: row, intent: 'suspend' })}
              >
                Suspend
              </Button>
            )}
            <Button
              size="sm"
              variant="danger"
              onClick={() => setPendingAction({ member: row, intent: 'remove' })}
            >
              Remove
            </Button>
          </div>
        );
      },
    },
  ];

  const invitationColumns: ReadonlyArray<Column<InvitationResource>> = [
    { key: 'email', header: 'Email', render: (row) => row.email },
    { key: 'role', header: 'Role', width: 'w-40', render: (row) => roleLabel(row.role) },
    {
      key: 'status',
      header: 'Status',
      width: 'w-32',
      render: (row) => <StatusBadge tone={INVITATION_TONE[row.status]}>{row.status}</StatusBadge>,
    },
    {
      key: 'expires',
      header: 'Expires',
      width: 'w-32',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{formatDate(row.expires_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: 'w-28',
      render: (row) =>
        row.status === 'PENDING' && canInvite ? (
          <Button
            size="sm"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate(row.invitation_id)}
          >
            Revoke
          </Button>
        ) : null,
    },
  ];

  const actionFailure =
    setStatus.isError && pendingAction !== null ? describeFailure(setStatus.error) : null;
  const roleFailure = changeRole.isError ? describeFailure(changeRole.error) : null;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Team"
        description={`People with access to ${context.organization?.name ?? 'this organization'}.`}
        breadcrumbs={breadcrumbs}
        linkComponent={Link}
        actions={
          canInvite ? (
            <Button variant="primary" icon={<UserPlus />} onClick={() => setInviteOpen(true)}>
              Invite member
            </Button>
          ) : null
        }
      />

      {roleFailure ? (
        <div
          role="alert"
          className="rounded-[var(--vc-radius-md)] border border-[var(--vc-danger-text)] bg-[var(--vc-surface)] px-4 py-3 text-[13px] text-[var(--vc-danger-text)]"
        >
          {roleFailure.message}
          {roleFailure.requestId ? (
            <span className="ml-2 text-[var(--vc-text-subtle)]">
              Request <Mono>{roleFailure.requestId}</Mono>
            </span>
          ) : null}
        </div>
      ) : null}

      <SectionCard
        title="Members"
        description="Everyone who can sign in to this organization."
        flush
      >
        {members.isPending ? (
          <LoadingState label="Loading members" />
        ) : members.isError ? (
          <ErrorState
            description={describeFailure(members.error).message}
            requestId={describeFailure(members.error).requestId}
            onRetry={() => void members.refetch()}
          />
        ) : (
          <ResourceTable
            caption="Members of this organization"
            columns={memberColumns}
            rows={members.data.data.data}
            rowKey={(row) => row.membership_id}
            empty={
              <EmptyState
                icon={<Users />}
                title="No members yet"
                description="Invite someone to give them access to this organization."
              />
            }
          />
        )}
      </SectionCard>

      <SectionCard
        title="Invitations"
        description="Invitations expire after seven days. Revoking one takes effect immediately."
        flush
      >
        {invitations.isPending ? (
          <LoadingState label="Loading invitations" />
        ) : invitations.isError ? (
          <ErrorState
            description={describeFailure(invitations.error).message}
            requestId={describeFailure(invitations.error).requestId}
            onRetry={() => void invitations.refetch()}
          />
        ) : (
          <ResourceTable
            caption="Invitations to this organization"
            columns={invitationColumns}
            rows={invitations.data.data.data}
            rowKey={(row) => row.invitation_id}
            empty={
              <EmptyState
                icon={<Mail />}
                title="No invitations"
                description="Invitations you send appear here until they are accepted, revoked or expire."
              />
            }
          />
        )}
      </SectionCard>

      {organizationId !== null ? (
        <InviteDialog
          organizationId={organizationId}
          roles={roles}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      ) : null}

      {pendingAction !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setStatus.reset();
              setPendingAction(null);
            }
          }}
          title={
            pendingAction.intent === 'remove'
              ? `Remove ${pendingAction.member.display_name}?`
              : pendingAction.intent === 'suspend'
                ? `Suspend ${pendingAction.member.display_name}?`
                : `Reactivate ${pendingAction.member.display_name}?`
          }
          description={
            pendingAction.intent === 'remove'
              ? 'They lose access to this organization immediately. They can be invited again later.'
              : pendingAction.intent === 'suspend'
                ? 'They keep their account but lose access to this organization until reactivated.'
                : 'They regain the access they had before being suspended.'
          }
          confirmLabel={
            pendingAction.intent === 'remove'
              ? 'Remove member'
              : pendingAction.intent === 'suspend'
                ? 'Suspend member'
                : 'Reactivate member'
          }
          destructive={pendingAction.intent === 'remove'}
          pending={setStatus.isPending}
          error={actionFailure}
          onConfirm={() =>
            setStatus.mutate(
              {
                membershipId: pendingAction.member.membership_id,
                intent: pendingAction.intent,
              },
              { onSuccess: () => setPendingAction(null) },
            )
          }
        />
      ) : null}
    </div>
  );
}

export const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  component: TeamPage,
});
